import { CloseCircleFilled } from "@ant-design/icons";
import type { TableColumnsType } from "antd";
import { activityLogApi } from "api/activityLog.api";
import { staffApi } from "api/staff.api";
import { AppTable, AppTableToolbar } from "components/antd-ui";
import {
  CustomerCell,
  TableLayout,
  TableLayoutFooter,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ActivityLog, ActivityLogLocationState, ActivityLogRefType, activityLogRefTypeLabel } from "types/activityLog";
import { QueryParam } from "types/query";
import { Staff } from "types/staff";
import { getTitle } from "utils";
import { formatDateTime } from "utils/date";
import type { Dayjs } from "utils/dayjs";
import { OPPORTUNITY_PATH } from "views/Opportunity/opportunity.meta";
import { ORDER_PATH } from "views/Order/order.meta";
import "./Log.scss";

type StaffOption = { label: string; value: number };

const objectLink = (to: string, label?: string | null, state?: ActivityLogLocationState) => {
  const text = (label || "").trim();
  if (!text) {
    return "--";
  }

  return (
    <Link className="activityLogObjectLink" to={to} state={state}>
      {text}
    </Link>
  );
};

const renderObject = (record: ActivityLog) => {
  switch (record.refType) {
    case "ORDER":
      return objectLink(`${ORDER_PATH}?orderId=${record.order?.id || record.refId}`, record.order?.code);
    case "OPPORTUNITY":
      return objectLink(`${OPPORTUNITY_PATH}/${record.opportunity?.id || record.refId}`, record.opportunity?.code);
    case "CUSTOMER":
      return record.customer
        ? objectLink("/customer", record.customer.code, { customer: record.customer })
        : "--";
    case "LEAD":
      return record.lead
        ? objectLink("/lead", record.lead.name, { lead: record.lead })
        : "--";
    case "TODO":
      return record.todo
        ? objectLink("/todo", record.todo.name, { todo: record.todo })
        : "--";
    case "FEEDBACK":
      return record.feedback
        ? objectLink("/feedback", record.feedback.content, { feedback: record.feedback })
        : "--";
    case "COMMENT":
      return "Bình luận";
    case "STAFF":
      return record.refStaff?.name || record.refStaff?.username || "--";
    case "ROLE":
      return record.role?.name || "--";
    default:
      return "--";
  }
};

export const LogPage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 50,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ActivityLog[]>([]);
  const [total, setTotal] = useState(0);
  const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
  const selectedStaffRef = useRef<StaffOption>();
  const staffOptionsRef = useRef<StaffOption[]>([]);
  staffOptionsRef.current = staffOptions;

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const fetchData = useCallback(async (queryParams: QueryParam) => {
    setLoading(true);
    try {
      const res = await activityLogApi.findAll(queryParams);
      setData(res.data.activityLogs);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(query);
  }, [fetchData, query]);

  const debounceSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        setQuery((prev) => ({ ...prev, search: keyword, page: 1 }));
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debounceSearch.cancel();
    };
  }, [debounceSearch]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      debounceSearch(value);
    },
    [debounceSearch],
  );

  const handleClearSearch = useCallback(() => {
    setSearch("");
    debounceSearch.cancel();
    setQuery((prev) => ({ ...prev, page: 1, search: "" }));
  }, [debounceSearch]);

  const handlePageChange = useCallback(({ limit, page }: { limit: number; page: number }) => {
    setQuery((prev) => ({ ...prev, page, limit }));
  }, []);

  const fetchStaff = useCallback(async (keyword: string) => {
    const res = await staffApi.findAll({ page: 1, limit: 20, search: keyword });
    const staffs: Staff[] = res.data.staffs || [];
    const options = staffs.map((staff) => ({
      label: staff.name || staff.username,
      value: staff.id,
    }));
    const selected = selectedStaffRef.current;
    if (selected && !options.some((option) => option.value === selected.value)) {
      setStaffOptions([selected, ...options]);
      return;
    }
    setStaffOptions(options);
  }, []);

  const debounceSearchStaff = useMemo(
    () =>
      debounce((keyword: string) => {
        fetchStaff(keyword);
      }, 500),
    [fetchStaff],
  );

  useEffect(() => {
    fetchStaff("");
    return () => {
      debounceSearchStaff.cancel();
    };
  }, [debounceSearchStaff, fetchStaff]);

  const handleStaffChange = useCallback((value: string | number | undefined) => {
    const staffId = typeof value === "number" ? value : undefined;
    selectedStaffRef.current = staffId
      ? staffOptionsRef.current.find((option) => option.value === staffId)
      : undefined;
    setQuery((prev) => {
      const next: QueryParam = { ...prev, page: 1 };
      if (staffId) {
        next.staffId = staffId;
      } else {
        delete next.staffId;
      }
      return next;
    });
  }, []);

  const handleDateChange = useCallback((values: [Dayjs | null, Dayjs | null] | null) => {
    setDateRange(values);
    setQuery((prev) => {
      const next: QueryParam = { ...prev, page: 1 };
      if (values?.[0] && values?.[1]) {
        next.fromAt = values[0].startOf("day").unix();
        next.toAt = values[1].endOf("day").unix();
      } else {
        delete next.fromAt;
        delete next.toAt;
      }
      return next;
    });
  }, []);

  const filters = useMemo(
    () => [
      {
        key: "staffId",
        value: (query.staffId as number | undefined) ?? "",
        options: staffOptions,
        onChange: handleStaffChange,
        placeholder: "Người thao tác",
        allowClear: true,
        showSearch: true,
        filterOption: false as const,
        onSearch: debounceSearchStaff,
        width: 220,
      },
      {
        type: "dateRange" as const,
        key: "dateRange",
        value: dateRange,
        onChange: handleDateChange,
        width: 280,
      },
    ],
    [dateRange, debounceSearchStaff, handleDateChange, handleStaffChange, query.staffId, staffOptions],
  );

  const columns = useMemo<TableColumnsType<ActivityLog>>(
    () => [
      {
        title: "Thời gian",
        dataIndex: "createdAt",
        width: 180,
        render: (createdAt: number) => formatDateTime(createdAt),
      },
      {
        title: "Người thao tác",
        width: 200,
        render: (_: unknown, record: ActivityLog) => record.staff?.name || record.staff?.username || "--",
      },
      {
        title: "Khách hàng",
        key: "customer",
        width: 220,
        render: (_: unknown, record: ActivityLog) => (
          <CustomerCell
            customer={record.customer}
            href={record.customer?.id ? `/customer?customerId=${record.customer.id}` : undefined}
          />
        ),
      },
      {
        title: "Loại",
        dataIndex: "refType",
        width: 160,
        render: (refType: ActivityLogRefType) => activityLogRefTypeLabel[refType] || refType || "--",
      },
      {
        title: "Đối tượng",
        key: "object",
        width: 180,
        ellipsis: true,
        render: (_: unknown, record: ActivityLog) => renderObject(record),
      },
      {
        title: "Mô tả",
        dataIndex: "description",
        ellipsis: true,
      },
    ],
    [],
  );

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  return (
    <TableLayout
      filter={
        <AppTableToolbar
          search={{
            value: search,
            width: 320,
            placeholder: "Tìm theo mô tả",
            onChange: handleSearchChange,
            inputProps: {
              allowClear: {
                clearIcon: <CloseCircleFilled onClick={handleClearSearch} />,
              },
            },
          }}
          filterLabel={null}
          filters={filters}
        />
      }
      table={
        <AppTable<ActivityLog>
          rowKey="id"
          loading={loading}
          pagination={false}
          dataSource={data}
          columns={columns}
        />
      }
      footer={
        <TableLayoutFooter
          showingFrom={showingFrom}
          showingTo={showingTo}
          total={total}
          currentPage={query.page}
          pageSize={query.limit}
          onChange={handlePageChange}
        />
      }
    />
  );
});
